<template>
  <div>
    <CategoryView
      :publications="publications"
      :searcher="searcher"
      :sort-strategy="sortStrategy"
      :custom-counter="customCounter"
    />
    <AppFooter />
  </div>
</template>

<script>
import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('category_name.vue');
import pageCommonMixin from '@/components/mixins/pageCommonMixin';
import publicationUtils from '@/services/utils/publicationUtils';

import AppStateEnum from '@/enums/AppStateEnum';
import CustomCategoriesEnum from '@shared/enums/CustomCategoriesEnum';
import ManagePublicationsStates from '@/enums/ManagePublicationsStatesEnum';
import SearchPublicationsFactory from '@/classes/factories/SearchPublicationsFactory';

import CategoryView from '@/components/views/CategoryView/CategoryView.vue';
import AppFooter from '@/components/views/AppFooter/AppFooter.vue';
import BuildValidateService from '@/services/BuildValidateService';

function getSearcher(store, category, from) {
  const currentLanguage = store.getters['ContextStore/currentLanguageGetter'];
  const librarySearcher = store.getters['LibraryStore/getLibrarySearcher'];
  const categorySearcher = store.getters['LibraryStore/getCategorySearcher'];

  let newSearcher;
  if (
    from?.name === AppStateEnum.MANAGE_PUBLICATION ||
    from?.name === AppStateEnum.MANAGE_PUBLICATION_LANGUAGE
  ) {
    newSearcher = SearchPublicationsFactory.searcherToBuilder(librarySearcher)
      .setCategories([category])
      .build();
  } else {
    newSearcher = SearchPublicationsFactory.searcherToBuilder(categorySearcher)
      .setState(ManagePublicationsStates.LIBRARY)
      .setLanguage(currentLanguage)
      .setCategories([category])
      .build();
  }
  return newSearcher;
}

async function libraryState(store) {
  if (process.server) {
    store.dispatch('LibraryStore/destroyStates', [
      ManagePublicationsStates.LIBRARY
    ]);
  }
  await Promise.all([
    store.dispatch('LibraryStore/getNewBookIds'),
    store.dispatch('LibraryStore/initState', {
      managePublicationsState: ManagePublicationsStates.LIBRARY
    })
  ]);
}

function getPublications(store, searcher, category) {
  const publications = store.getters['LibraryStore/searchLibraryBooks'](
    searcher,
    false
  );
  logger.info(
    `loaded publications ${publications.length} for category ${category}`
  );
  BuildValidateService.checkCategoryState(searcher, publications, store);
  return publications;
}

function chooseSortStrategy(store, category) {
  const sortStrategy =
    category === CustomCategoriesEnum.RECENT
      ? publicationUtils.sortPublicationEnum.LAST_READING_TIME
      : publicationUtils.sortPublicationEnum.ALPHABET;
  return sortStrategy;
}

export default {
  name: 'CategoryPage',
  components: { CategoryView, AppFooter },
  mixins: [pageCommonMixin],
  async asyncData({ store, params, from }) {
    try {
      const runId = store.getters['UserStore/getRunId'];
      logger.info(
        `start load publications for category: ${params.name} with runId ${runId}`
      );
      const [category, language] = params.name.split('_');
      store.commit('ContextStore/setCurrentLanguage', language || 'en');
      await libraryState(store);
      const { link, meta } = await store.dispatch(
        'ContextStore/getCategoryPageHeadInfo',
        params.name
      );
      const searcher = getSearcher(store, category, from);
      store.commit('LibraryStore/setCategorySearcher', searcher);
      let customCounter;
      let publications = getPublications(store, searcher, category);
      const sortStrategy = chooseSortStrategy(store, category);
      if (process.server && publications?.length) {
        const authorList = publicationUtils.createAuthorList(
          publications,
          publications[0]?.category
        );

        store.commit('LibraryStore/setServerSideAuthorList', {
          authorList
        });

        customCounter = publicationUtils.countPublicationItems(publications);
        publications = publicationUtils.sortPublication(
          publications,
          sortStrategy
        );
        publications = publications.slice(0, 15);
        store.commit('LibraryStore/destroyLibrary');
      }
      await store.dispatch('LibraryStore/loadPublicationCategoryInfo');
      return {
        publications,
        searcher,
        sortStrategy,
        link,
        category,
        customCounter,
        meta
      };
    } catch (error) {
      logger.fatal(`Get error on create category page error: ${error}`);
    }
  },
  data() {
    return {
      searcher: this.searcher,
      publications: this.publications,
      sortStrategy: this.sortStrategy
    };
  },
  head() {
    const headSettings = this.getCommonHeadSettings();

    return {
      ...headSettings,
      title: this.capitalizeFirstLetter(this.category),
      link: this.link,
      meta: this.meta
    };
  },
  mounted() {
    this.$store.subscribe(mutation => {
      if (mutation.type === 'ContextStore/setServiceClientInit') {
        this.customCounter = null;
      }
    });
  },
  methods: {
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    }
  }
};
</script>

<style></style>
